import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const WELCOME_POPUP_KEY = "valorform_welcome_dismissed";

export const WelcomePopup = () => {
  const [open, setOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Only show once per browser — persist dismissal so scrolling or route
    // changes never re-trigger the popup on mobile.
    try {
      if (window.localStorage.getItem(WELCOME_POPUP_KEY)) return;
    } catch {
      /* localStorage unavailable — fall through and still show once per session */
    }
    // Delay past LCP window; longer on mobile to protect performance metrics.
    const isMobile = window.innerWidth < 768;
    const delay = isMobile ? 6000 : 1500;
    const id = window.setTimeout(() => setOpen(true), delay);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => setShowContent(true), 100);
    } else {
      setShowContent(false);
    }
  }, [open]);

  const handleClose = () => {
    setIsClosing(true);
    try {
      window.localStorage.setItem(WELCOME_POPUP_KEY, "1");
    } catch {
      /* ignore storage errors */
    }
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-2 border-primary">
        <VisuallyHidden.Root>
          <DialogTitle>Valorform welcome dialog</DialogTitle>
          <DialogDescription>Ready to transform your performance?</DialogDescription>
        </VisuallyHidden.Root>
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          aria-label="Close welcome dialog"
          className={`absolute top-3 right-3 z-10 rounded-full bg-background/80 hover:bg-background transition-all duration-300 ${
            showContent && !isClosing ? "opacity-100 scale-100" : "opacity-0 scale-75"
          }`}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>

        <div className={`relative bg-gradient-to-br from-primary/20 to-background p-12 text-center transition-all duration-300 ${
          isClosing ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}>
          {/* Animated Football */}
          <div 
            className={`relative mb-6 transition-all duration-700 ease-out ${
              showContent && !isClosing ? "opacity-100 scale-100" : "opacity-0 scale-50"
            }`}
          >
            <svg 
              className="h-32 w-32 mx-auto text-primary animate-bounce"
              viewBox="0 0 100 100"
              fill="currentColor"
            >
              <circle cx="50" cy="50" r="45" fill="currentColor"/>
              <path d="M 50,5 L 50,95 M 5,50 L 95,50" stroke="hsl(0 0% 10%)" strokeWidth="3" fill="none"/>
              <path d="M 17,17 Q 30,30 50,30 Q 70,30 83,17" stroke="hsl(0 0% 10%)" strokeWidth="3" fill="none"/>
              <path d="M 83,17 Q 70,30 70,50 Q 70,70 83,83" stroke="hsl(0 0% 10%)" strokeWidth="3" fill="none"/>
              <path d="M 83,83 Q 70,70 50,70 Q 30,70 17,83" stroke="hsl(0 0% 10%)" strokeWidth="3" fill="none"/>
              <path d="M 17,83 Q 30,70 30,50 Q 30,30 17,17" stroke="hsl(0 0% 10%)" strokeWidth="3" fill="none"/>
            </svg>
          </div>
          
          {/* Greeting */}
          <h2 
            className={`text-3xl font-bold text-foreground mb-4 transition-all duration-500 delay-200 ${
              showContent && !isClosing ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Welcome to Valorform! ⚽
          </h2>
          <p 
            className={`text-lg text-muted-foreground mb-4 transition-all duration-500 delay-300 ${
              showContent && !isClosing ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Ready to transform your performance?
          </p>
          <p 
            className={`text-sm text-muted-foreground italic transition-all duration-500 delay-500 ${
              showContent && !isClosing ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            "Champions aren't built in a day, but every day builds a champion."
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
